import { diagnosticOutcome, diagnosticTransportOutcome, registerTradeTransport, tradeProviderInfo, tradeSpan } from "./trade-diagnostics";
import type { Eip1193Provider } from "./eip1193";

const methods = new Set(["eth_chainId", "eth_getBlockByNumber", "eth_getBlockByHash", "eth_getCode", "eth_getStorageAt",
  "eth_call", "eth_getBalance", "eth_getTransactionReceipt", "eth_getTransactionByHash",
  "eth_getLogs", "eth_estimateGas", "eth_gasPrice"]);

export function assertReadMethod(method: string, label: string) {
  if (!methods.has(method)) throw new Error(`${label}_READ_ONLY_RPC`);
}

export function readRpcUrl(raw: string | undefined): URL | null {
  if (!raw?.trim()) return null;
  try {
    const url = new URL(raw.trim());
    if (url.username || url.password || url.hash || !(url.protocol === "https:" ||
      url.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname))) return null;
    return url;
  } catch { return null; }
}

/** HTTP observation only. Never accepts accounts, signatures, sends or provider-specific queue methods. */
export function httpReadProvider(url: URL, label: string, signal?: AbortSignal): Eip1193Provider {
  const provider: Eip1193Provider = { request: async ({ method, params }) => {
    signal?.throwIfAborted();
    assertReadMethod(method, label);
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 15_000);
    const cancel = () => abort.abort(signal?.reason);
    signal?.addEventListener("abort", cancel, { once: true });
    const end = tradeSpan(undefined, "http", { ...tradeProviderInfo(provider), methods: [method], callCount: 1 });
    try {
      const response = await fetch(url.href, { method: "POST", signal: abort.signal, credentials: "omit",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params: params ?? [] }) });
      if (!response.ok) throw new Error(`${label}_RPC_UNAVAILABLE`);
      const payload: unknown = await response.json();
      if (!payload || typeof payload !== "object" || Array.isArray(payload) ||
        !("jsonrpc" in payload) || payload.jsonrpc !== "2.0" || !("id" in payload) || payload.id !== 1 ||
        "error" in payload && "result" in payload) throw new Error(`${label}_RPC_INVALID_RESPONSE`);
      if ("error" in payload) {
        const error = payload.error;
        if (!error || typeof error !== "object" || !("code" in error) || typeof error.code !== "number" ||
          !("message" in error) || typeof error.message !== "string") throw new Error(`${label}_RPC_INVALID_RESPONSE`);
        // Preserve revert data for the existing contract-error decoder and claim recovery.
        throw Object.assign(new Error(error.message), { code: error.code, ...("data" in error ? { data: error.data } : {}) });
      }
      if (!("result" in payload)) throw new Error(`${label}_RPC_INVALID_RESPONSE`);
      return payload.result;
    } catch (error) { end(abort.signal.aborted && !signal?.aborted ? "timeout" : diagnosticOutcome(error)); throw error; }
    finally { end(); clearTimeout(timer); signal?.removeEventListener("abort", cancel); }
  } };
  return registerTradeTransport(provider, "http", url);
}

/** Operation-local batching; no wallet methods, retries, or completed-result cache. */
export function httpBatchReadProvider(url: URL, label: string, signal?: AbortSignal): Eip1193Provider {
  type Pending = { id: number; method: string; params: readonly unknown[] | object;
    resolve(value: unknown): void; reject(error: unknown): void };
  let queue: Pending[] = [], nextId = 1;
  const flush = async (batch: Pending[]) => {
    const abort = new AbortController();
    const cancel = () => abort.abort(signal?.reason);
    const timer = setTimeout(() => abort.abort(), 15_000);
    signal?.addEventListener("abort", cancel, { once: true });
    let end: ReturnType<typeof tradeSpan> | undefined;
    try {
      signal?.throwIfAborted();
      const requests = batch.map(({ id, method, params }) => ({ jsonrpc: "2.0", id, method, params }));
      const init: RequestInit = { method: "POST", signal: abort.signal, credentials: "omit",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(batch.length === 1 ? requests[0] : requests) };
      end = tradeSpan(undefined, "http", { ...tradeProviderInfo(provider), methods: batch.map(item => item.method), callCount: batch.length });
      const response = await fetch(url.href, init);
      if (!response.ok) throw new Error(`${label}_RPC_UNAVAILABLE`);
      const payload: unknown = await response.json();
      signal?.throwIfAborted(); abort.signal.throwIfAborted();
      const replies = batch.length === 1 ? [payload] : Array.isArray(payload) ? payload : [];
      const ids = new Set(batch.map(item => item.id));
      const decoded = new Map<number, { result?: unknown; error?: Error }>();
      for (const reply of replies) {
        if (!reply || typeof reply !== "object" || !("jsonrpc" in reply) || reply.jsonrpc !== "2.0" ||
          !("id" in reply) || typeof reply.id !== "number" || !ids.has(reply.id) || decoded.has(reply.id) ||
          ("result" in reply) === ("error" in reply)) throw new Error(`${label}_RPC_INVALID_BATCH`);
        if ("error" in reply) {
          const error = reply.error;
          if (!error || typeof error !== "object" || !("code" in error) || typeof error.code !== "number" ||
            !("message" in error) || typeof error.message !== "string") throw new Error(`${label}_RPC_INVALID_BATCH`);
          decoded.set(reply.id, { error: Object.assign(new Error(error.message),
            { code: error.code, ...("data" in error ? { data: error.data } : {}) }) });
        } else if ("result" in reply) decoded.set(reply.id, { result: reply.result });
      }
      if (decoded.size !== batch.length) throw new Error(`${label}_RPC_INVALID_BATCH`);
      for (const item of batch) {
        const result = decoded.get(item.id)!;
        if (result.error) item.reject(result.error); else item.resolve(result.result);
      }
    } catch (error) { end?.(diagnosticTransportOutcome(error, signal, abort.signal)); for (const item of batch) item.reject(error); }
    finally { end?.(); clearTimeout(timer); signal?.removeEventListener("abort", cancel); }
  };
  const provider: Eip1193Provider = { request: async ({ method, params }) => {
    signal?.throwIfAborted(); assertReadMethod(method, label);
    return new Promise((resolve, reject) => {
      queue.push({ id: nextId++, method, params: params ?? [], resolve, reject });
      if (queue.length === 1) queueMicrotask(() => {
        const pending = queue; queue = [];
        // Bounded request bodies, matching the application's Elysium batch limit.
        for (let offset = 0; offset < pending.length; offset += 16) {
          const batch = pending.slice(offset, offset + 16);
          flush(batch).catch(error => { for (const item of batch) item.reject(error); });
        }
      });
    });
  } };
  return registerTradeTransport(provider, "http-batch", url);
}
